'use client';

import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const WELCOME_MESSAGE: Message = {
  role: 'assistant',
  content:
    "Bonjour, je suis ASMAO, l'assistant virtuel de la maison. Je suis à votre disposition pour vous renseigner sur nos collections de cuir, notre savoir-faire artisanal, ou pour toute demande personnalisée. Comment puis-je vous aider aujourd'hui ?",
};

const QUICK_PROMPTS = [
  'Quelles sont vos collections ?',
  'Où se trouve la boutique ?',
  'Comment commander sur mesure ?',
  'Quels sont vos délais ?',
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, loading]);

  // Show welcome tooltip after 4 seconds if not opened
  useEffect(() => {
    const t = setTimeout(() => {
      if (!hasInteracted) setShowTooltip(true);
    }, 4000);
    const t2 = setTimeout(() => setShowTooltip(false), 11000);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [hasInteracted]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const sendMessage = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    setHasInteracted(true);
    setShowTooltip(false);
    const userMsg: Message = { role: 'user', content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const apiMessages = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });
      const data = await res.json();
      const reply: string = data.reply || data.error || "Je suis désolé, je n'ai pas pu répondre.";
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "Je vous prie de m'excuser, une erreur technique est survenue. Vous pouvez nous joindre directement au +33 6 99 70 39 84 ou via le formulaire de contact.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([WELCOME_MESSAGE]);
    setHasInteracted(false);
  };

  return (
    <>
      {/* Floating action button + tooltip */}
      <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3 pointer-events-none">
        {/* Tooltip */}
        {showTooltip && !open && (
          <div className="pointer-events-auto bg-[#2B1810] border border-[#C9A961]/40 rounded-lg shadow-2xl px-4 py-3 max-w-[240px] relative animate-fade-up">
            <button
              onClick={() => setShowTooltip(false)}
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#C9A961] text-[#2B1810] flex items-center justify-center text-xs"
              aria-label="Fermer"
            >
              ×
            </button>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={12} className="text-[#C9A961]" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#C9A961] font-medium">
                ASMAO · Assistant
              </span>
            </div>
            <p className="text-[#F8F1E4] text-xs leading-relaxed">
              Une question sur nos collections ? Je suis à votre service.
            </p>
            {/* Arrow */}
            <div className="absolute -bottom-1 right-8 w-3 h-3 bg-[#2B1810] border-r border-b border-[#C9A961]/40 rotate-45" />
          </div>
        )}

        {/* Main button */}
        <button
          onClick={() => setOpen(!open)}
          className="pointer-events-auto group relative w-14 h-14 rounded-full bg-gradient-to-br from-[#C9A961] to-[#D4A017] shadow-[0_8px_32px_rgba(212,160,23,0.5)] hover:shadow-[0_12px_40px_rgba(212,160,23,0.65)] transition-all duration-500 hover:scale-105 flex items-center justify-center"
          aria-label={open ? 'Fermer le chat' : 'Ouvrir le chat'}
        >
          {/* Pulsing ring */}
          {!open && (
            <span className="absolute inset-0 rounded-full border-2 border-[#C9A961] animate-ping opacity-30" />
          )}
          {/* Icon swap */}
          {open ? (
            <X size={22} className="text-[#2B1810] transition-transform duration-300 rotate-0" />
          ) : (
            <MessageCircle
              size={24}
              className="text-[#2B1810] transition-transform duration-300 group-hover:scale-110"
              fill="currentColor"
            />
          )}
        </button>
      </div>

      {/* Chat panel */}
      <div
        className={`fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] max-w-[400px] origin-bottom-right transition-all duration-300 ${
          open
            ? 'opacity-100 scale-100 pointer-events-auto'
            : 'opacity-0 scale-95 pointer-events-none translate-y-4'
        }`}
      >
        <div className="bg-[#F8F1E4] rounded-2xl shadow-2xl border border-[#C9A961]/30 overflow-hidden flex flex-col" style={{ height: 'min(70vh, 600px)' }}>
          {/* Header */}
          <div className="bg-gradient-to-br from-[#2B1810] to-[#5C2E03] px-5 py-4 flex items-center justify-between border-b border-[#C9A961]/30">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full border border-[#C9A961] bg-[#2B1810] flex items-center justify-center">
                  <span className="font-display text-[#C9A961] text-base font-bold tracking-wider">A</span>
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#2B1810]" />
              </div>
              <div>
                <h3 className="font-display text-[#F8F1E4] text-base tracking-[0.1em] font-semibold">
                  ASMAO
                </h3>
                <p className="text-[#C9A961]/90 text-[10px] tracking-[0.25em] uppercase font-serif-display">
                  Assistant Virtuel · En ligne
                </p>
              </div>
            </div>
            <button
              onClick={resetChat}
              className="text-[#C9A961]/70 hover:text-[#C9A961] text-[10px] uppercase tracking-[0.15em] transition-colors"
              title="Recommencer la conversation"
            >
              Reset
            </button>
          </div>

          {/* Messages area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F8F1E4] scroll-smooth"
            style={{
              backgroundImage:
                'radial-gradient(circle at 25% 25%, rgba(201,169,97,0.04) 0%, transparent 60%), radial-gradient(circle at 75% 75%, rgba(139,69,19,0.04) 0%, transparent 60%)',
            }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                    m.role === 'user'
                      ? 'bg-gradient-to-br from-[#C9A961] to-[#D4A017] text-[#2B1810] rounded-br-sm font-medium'
                      : 'bg-white border border-[#C9A961]/20 text-[#2B1810] rounded-bl-sm shadow-sm'
                  }`}
                  style={{
                    fontFamily: m.role === 'assistant' ? 'Georgia, serif' : 'inherit',
                  }}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#C9A961]/20 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                  <div className="flex gap-1.5 items-center">
                    <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Quick prompts (only show if first interaction) */}
            {messages.length === 1 && !loading && (
              <div className="pt-3 space-y-2">
                <p className="text-center text-[10px] uppercase tracking-[0.25em] text-[#8B4513]/60 font-medium">
                  Suggestions
                </p>
                <div className="flex flex-col gap-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => sendMessage(p)}
                      className="text-left text-xs px-3 py-2 bg-white border border-[#C9A961]/30 rounded-lg hover:border-[#C9A961] hover:bg-[#C9A961]/5 text-[#2B1810] transition-all duration-300 group flex items-center gap-2"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#C9A961] group-hover:scale-150 transition-transform" />
                      <span className="flex-1">{p}</span>
                      <span className="text-[#C9A961] opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="border-t border-[#C9A961]/20 bg-white px-3 py-3">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Écrivez votre message…"
                disabled={loading}
                className="flex-1 bg-[#F8F1E4] border border-[#C9A961]/30 rounded-full px-4 py-2.5 text-sm text-[#2B1810] placeholder-[#8B4513]/40 focus:outline-none focus:border-[#C9A961] focus:bg-white transition-all duration-300 disabled:opacity-50"
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br from-[#2B1810] to-[#5C2E03] hover:from-[#C9A961] hover:to-[#D4A017] text-[#C9A961] hover:text-[#2B1810] flex items-center justify-center transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed group"
                aria-label="Envoyer"
              >
                <Send size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </div>
            <p className="text-center text-[9px] text-[#8B4513]/40 mt-2 tracking-[0.15em] uppercase">
              Réponse automatique · Pour commande, contactez la boutique
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
